import _ from 'lodash';
import {
  DataFeedSubscription,
  QuoteFeedType,
  SpreadFeedType,
  OrderUpdateFeedType,
  IFeedSpread,
  AssetType,
  SpreadType,
} from '@tradingblock/types';
import { isOCCSymbol, toPrefixedOccSymbol } from '@tradingblock/api';
import { isOptionPastCalendarExpirationDate } from '../../utilities/optionSymbolExpiration';

export const DATA_FEED_PREFIX = 'tradingblockfeed';

/**
 * Filters out option symbols whose expiration calendar date is before today.
 */
function filterExpiredOptionSymbols(symbols: string[]): string[] {
  return symbols.filter(symbol => {
    // Strip leading dot if present (e.g., ".TSLA 251121C432500" -> "TSLA 251121C432500")
    const cleanSymbol = symbol.startsWith('.') ? symbol.substring(1) : symbol;
    // Only check expiration for OCC symbols (options)
    if (isOCCSymbol(cleanSymbol)) {
      return !isOptionPastCalendarExpirationDate(cleanSymbol);
    }
    // Non-option symbols are always included
    return true;
  });
}

/**
 * Filters out spreads that contain option legs past calendar expiration.
 */
function filterExpiredOptionSpreads(spreads: IFeedSpread[]): IFeedSpread[] {
  return spreads.filter(spread => {
    // Check each leg in the spread
    const hasExpiredLeg = spread.Legs.some(leg => {
      // Strip leading dot if present (e.g., ".TSLA 251121C432500" -> "TSLA 251121C432500")
      const symbol = leg.Symbol.startsWith('.') ? leg.Symbol.substring(1) : leg.Symbol;
      // Only check expiration for options
      if (leg.AssetType === AssetType.Option) {
        return isOptionPastCalendarExpirationDate(symbol);
      }
      return false;
    });
    return !hasExpiredLeg;
  });
}

function getSubscriptionData(s: DataFeedSubscription) {
  switch (s.kind) {
    case 'Quote': {
      // Filter out expired option symbols before processing
      const filteredSymbols = filterExpiredOptionSymbols(s.symbols);
      const allSymbols = _.map(filteredSymbols, sym => (isOCCSymbol(sym) ? toPrefixedOccSymbol(sym) : sym)).join(',');
      return filteredSymbols.length > 0 ? `${QuoteFeedType}=${allSymbols}` : '';
    }
    case 'Spread': {
      // Filter out spreads with expired option legs before subscribing
      const filteredSpreads = filterExpiredOptionSpreads(s.spreadDefinition || []);
      return filteredSpreads && filteredSpreads.length > 0
        ? `${SpreadFeedType}=${JSON.stringify(filteredSpreads)}`
        : '';
    }
    case 'OrderUpdate':
      return `${OrderUpdateFeedType}=1`;
  }
}

function getNewSubscriptionData(s: DataFeedSubscription) {
  switch (s.kind) {
    case 'Quote': {
      // Filter out expired option symbols before processing
      const filteredSymbols = filterExpiredOptionSymbols(s.symbols);
      const allSymbols = _.map(filteredSymbols, sym => (isOCCSymbol(sym) ? toPrefixedOccSymbol(sym) : sym)); //_.filter(s.symbols, sym => !isOCCSymbol(sym));
      return filteredSymbols.length > 0 ? allSymbols : '';
    }
    case 'Spread':
      return s && s.spreadDefinition && s.spreadDefinition.length > 0 ? s.spreadDefinition.flat() : '';
    case 'OrderUpdate':
      return `1`;
  }
}

export const getQuoteSubscription = (
  apiToken: string,
  accountId: string | number,
  subs: DataFeedSubscription[],
  unsubscribe?: boolean
) => {
  const headerValues = [`0=${apiToken}`, `1=${accountId}`, unsubscribe ? '9=0' : '9=1'].filter(v => v !== undefined);
  const orderedSubscriptions = _.orderBy(subs, s => s)
    .map(ss => {
      // Filter spreads with expired option legs before processing
      if (ss.kind === 'Spread' && ss.spreadDefinition) {
        return {
          ...ss,
          spreadDefinition: filterExpiredOptionSpreads(ss.spreadDefinition),
        };
      }
      // Filter quotes with expired option symbols before processing
      if (ss.kind === 'Quote' && ss.symbols) {
        return {
          ...ss,
          symbols: filterExpiredOptionSymbols(ss.symbols),
        };
      }
      return ss;
    })
    .filter(ss =>
      ss.kind === 'Quote' ? ss.symbols.length > 0 : ss.kind === 'Spread' ? ss.spreadDefinition.length > 0 : true
    )
    .map(ss => getSubscriptionData(ss));
  if (apiToken && subs && orderedSubscriptions.length > 0) {
    const subscriptString = _.union(headerValues, orderedSubscriptions).join('|');
    return subscriptString;
  }
  return undefined;
};
export const getNewQuoteSubscription = (
  apiToken: string,
  accountId: string | number,
  subs: DataFeedSubscription[],
  unsubscribe?: boolean
) => {
  const orderedSubscriptions = _.orderBy(subs, s => s)
    .map(ss => {
      // For unsubscribes, don't filter expired options - we need to unsubscribe from them too
      // For subscribes, filter expired option symbols before processing
      if (ss.kind === 'Quote' && ss.symbols) {
        return {
          ...ss,
          symbols: unsubscribe ? ss.symbols : filterExpiredOptionSymbols(ss.symbols),
        };
      }
      return ss;
    })
    .filter(ss => (ss.kind === 'Quote' ? ss.symbols.length > 0 : false))
    .map(ss => getNewSubscriptionData(ss));
  if (apiToken && subs && orderedSubscriptions.length > 0) {
    const typeString = unsubscribe ? 'Unsubscribe' : 'Subscribe';
    return { type: typeString, symbols: orderedSubscriptions.flat() };
  }
  return undefined;
};
export const getNewSpreadsSubscription = (
  apiToken: string,
  accountId: string | number,
  subs: DataFeedSubscription[],
  unsubscribe?: boolean
) => {
  const orderedSubscriptions = _.orderBy(subs, s => s)
    .map(ss => {
      // Filter spreads with expired option legs before processing
      if (ss.kind === 'Spread' && ss.spreadDefinition) {
        return {
          ...ss,
          spreadDefinition: filterExpiredOptionSpreads(ss.spreadDefinition),
        };
      }
      return ss;
    })
    .filter(ss => (ss.kind === 'Spread' ? ss.spreadDefinition.length > 0 : false))
    .map(ss => getNewSubscriptionData(ss));
  if (apiToken && subs && orderedSubscriptions.length > 0) {
    const newMappedOrderedSubscriptions = orderedSubscriptions
      .flat()
      .map(
        (ss: any) => {
          const mapped = {
            id: ss.id,
            name: ss.name,
            type: typeof ss.type === 'number' ? SpreadType[ss.type] : ss.type,
            legs: mapNewLegs(ss.Legs),
          } as any;
          return mapped;
        }
      );
    const result = { type: unsubscribe ? 'Unsubscribe' : 'Subscribe', spreads: newMappedOrderedSubscriptions };
    return result;
  }
  return undefined;
};
function mapNewLegs(legs: any[]): any[] {
  return legs.map(leg => ({
    symbol: leg.Symbol,
    assetType: typeof leg.AssetType === 'number' ? AssetType[leg.AssetType] : leg.AssetType,
    quantity: leg.Quantity,
    multiplier: leg.Multiplier,
  }));
}
export const getOrderUpdateSubscription = (apiToken: string, accountId: string | number, unsubscribe?: boolean) => {
  const value = unsubscribe === true ? 0 : 1;
  return `0=${apiToken}|1=${accountId}|13=${value}`;
};
export const getCashieringUpdateSubscriptionCmd = (
  apiToken: string,
  accountIds: (string | number)[],
  unsubscribe?: boolean
) => `0=${apiToken}|1=${accountIds.join()}|15=${unsubscribe ? 2 : 1}`;

/*

  0 = 1 (Success)
  1 = 1 (i.e. single leg quote. any instrument)
  2 = QuoteTime
  3 = Symbol
  4 = LastTradePrice
  5 = BidPrice
  6 = AskPrice
  7 = NetChange
  8 = High
  9 = Low
  10 = Volume
  11 = Change Percentage

example:

  0=0|1=1|2=07/24/2019 18:35:38|3=TSLA|4=240.3|5=240.3|6=240.59|7=-19.87|8=266.07|9=258.1601|10=10494992|11=-7.64

*/

export const tryParseMessage = (message: string) => {};

// const parser = (value: string) => {
//   const vals = value.split('|').map(vv => vv.replace(/\d+=/, ''));
//   if (vals.length < 12) {
//     console.warn('quote string from feed has less fields than expected', value);
//   }
//   const str = (ind: number) => vals[ind];
//   const int = (ind: number) => parseInt(str(ind));
//   const float = (ind: number) => parseFloat(str(ind));
//   const success = (ind: number) => (int(ind) === 0 ? true : false);
//   const mtype = (typeVal: number): FeedMessageType => (typeVal === 0 ? 'HeartBeat' : typeVal === 1 ? 'Quote' : typeVal === 2 ? 'Spread' : typeVal === 3 ? 'OrderUpdate' : undefined);
//   const result = {
//     success: success(0),
//     type: mtype(1),
//   };
//   return {
//     str,
//     int,
//     float,
//     success,
//     mtype,
//     result,
//   };
// };

// export const parseQuote = (quote: string): FeedData<FeedQuote> => {
//   const { str, float, result } = parser(quote);
//   return {
//     ...result,
//     QuoteTime: str(2),
//     Symbol: str(3),
//     LastTradePrice: float(4),
//     BidPrice: float(5),
//     AskPrice: float(6),
//     NetChange: float(7),
//     High: float(8),
//     Low: float(9),
//     Volume: float(10),
//     ChangePercentage: float(11),
//     raw: quote,
//   };
// };
